import axios from 'axios';

import { WEBTOON_EPISODE_LIST_LIMIT, WEBTOON_STORY_TOON_IDS } from '@constants';
import { IEpisode, IEFinderObject, IPagination, IStory } from '@model';
import { toQueryString } from '@utils/misc';
import { getStoryToonId } from '@utils/webtoon';

import { api } from '.';

const STORY_TOON_ENDPOINT = '/story-toons';
const STORY_TOON_REQUEST_TIMEOUT = 8000;

export type StoryToonImage = IEFinderObject<'image'>;

export type StoryToonImageSlot =
  | StoryToonImage
  | {
      id?: number;
      order?: number;
      image?: StoryToonImage;
      file?: StoryToonImage;
    };

export interface IStoryToonStory
  extends Pick<IStory, 'id' | 'title' | 'summary' | 'coverImage'> {
  type?: string;
  theme?: string;
  saleType?: string;
  isFinished?: boolean;
  ageGrade?: string;
  user?: {
    id: number;
    nickname: string;
    picture?: unknown;
    hasHonorPartnership?: boolean;
  };
  tags?: Array<{
    id: number;
    name: string;
  }>;
  hasHonorPartnership?: boolean;
  isHonorPartnershipApplicant?: boolean;
}

export interface IStoryToon {
  id?: number;
  webtoonId?: number;
  storyToonId?: number;
  storyId?: number;
  story?: IStoryToonStory;
  title?: string;
  subtitle?: string;
  summary?: string;
  genre?: string;
  status?: string;
  published?: string;
  image?: StoryToonImage;
  mainImage?: StoryToonImage;
  coverImage?: StoryToonImage;
  thumbnailImage?: StoryToonImage;
  episodeCount?: number;
  firstEpisode?: IStoryToonEpisodeNavigation | null;
}

export interface IStoryToonEpisodeNavigation {
  id: number;
  episodeId?: number;
  title: string;
  published?: string;
  thumbnailImage?: StoryToonImage;
}

export interface IStoryToonEpisodeContent {
  key?: string;
  images?: StoryToonImage[];
  metadata?: {
    cutCount?: number;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface IStoryToonEpisode {
  id: number;
  storyToonId?: number;
  episodeId?: number;
  episodeNumber?: number;
  number?: number;
  order?: number;
  title: string;
  subtitle?: string;
  status?: string;
  published?: string;
  ageGrade?: string;
  commentCount?: number;
  isFree?: boolean;
  purchaseType?: IEpisode['purchaseType'];
  content?: IStoryToonEpisodeContent;
  prevEpisode?: IStoryToonEpisodeNavigation | null;
  nextEpisode?: IStoryToonEpisodeNavigation | null;
  thumbnailImage?: StoryToonImage;
  image?: StoryToonImage;
  imageFile?: StoryToonImage;
  contentImage?: StoryToonImage;
  images?: StoryToonImage[];
  episodeImages?: StoryToonImage[];
  toonImages?: StoryToonImage[];
  imageFiles?: StoryToonImage[];
  contentImages?: StoryToonImage[];
  files?: StoryToonImage[];
  cuts?: StoryToonImageSlot[];
  pages?: StoryToonImageSlot[];
}

type StoryToonListResponse = IStoryToon[] | IPagination<IStoryToon>;
type StoryToonEpisodeListResponse =
  | IStoryToonEpisode[]
  | IPagination<IStoryToonEpisode>;
export type StoryToonEpisodeOrdering = 'oldest' | 'newest';

const withStoryToonTimeout = async <T>(request: Promise<T>) => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error('StoryToon request timed out')),
      STORY_TOON_REQUEST_TIMEOUT
    );
  });

  try {
    return await Promise.race([request, timeout]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

const normalizeList = <T>(data: T[] | IPagination<T>) =>
  Array.isArray(data) ? data : data.items;

const isNotFoundError = (error: unknown) =>
  axios.isAxiosError(error) && error.response?.status === 404;

const isUnavailableStoryToonListError = (error: unknown) =>
  axios.isAxiosError(error) && [400, 404].includes(error.response?.status ?? 0);

export const fetchStoryToons = async () => {
  try {
    const response = await withStoryToonTimeout(
      api.get<StoryToonListResponse>(`${STORY_TOON_ENDPOINT}/`)
    );
    const storyToons = normalizeList(response.data);
    if (storyToons.length) {
      return storyToons;
    }
  } catch (error) {
    if (!isUnavailableStoryToonListError(error)) {
      throw error;
    }
  }

  try {
    const response = await withStoryToonTimeout(
      api.get<StoryToonListResponse>(
        `${STORY_TOON_ENDPOINT}/${toQueryString({
          storyToonIds: WEBTOON_STORY_TOON_IDS.join(','),
        })}`
      )
    );
    return normalizeList(response.data);
  } catch (fallbackError) {
    if (isUnavailableStoryToonListError(fallbackError)) {
      return [];
    }

    throw fallbackError;
  }
};

export const fetchStoryToon = async (webtoonId: number) => {
  try {
    const { data } = await withStoryToonTimeout(
      api.get<IStoryToon>(`${STORY_TOON_ENDPOINT}/${webtoonId}/`)
    );
    return data;
  } catch (error) {
    if (!isNotFoundError(error)) {
      throw error;
    }

    const storyToons = await fetchStoryToons().catch(() => []);
    const storyToon = storyToons.find(
      (item) =>
        getStoryToonId(item) === webtoonId ||
        item.storyId === webtoonId ||
        item.story?.id === webtoonId
    );

    if (!storyToon) {
      throw error;
    }

    return storyToon;
  }
};

export const fetchStoryToonEpisodes = async (
  storyToonId: number,
  ordering: StoryToonEpisodeOrdering = 'oldest'
) => {
  const { data } = await withStoryToonTimeout(
    api.get<StoryToonEpisodeListResponse>(
      `${STORY_TOON_ENDPOINT}/${storyToonId}/episodes/${toQueryString({
        ordering,
        page: 1,
        perPage: WEBTOON_EPISODE_LIST_LIMIT,
      })}`
    )
  );

  return normalizeList(data);
};

const fetchOriginalEpisodes = async (storyId: number) => {
  const fetchPage = async (page: number) => {
    const { data } = await withStoryToonTimeout(
      api.get<IPagination<IEpisode>>(
        `/stories/${storyId}/episodes/${toQueryString({
          ordering: 'oldest',
          page,
          perPage: WEBTOON_EPISODE_LIST_LIMIT,
        })}`
      )
    );

    return data;
  };

  const firstPage = await fetchPage(1);
  const remainingPages = await Promise.all(
    Array.from({ length: Math.max(firstPage.pages - 1, 0) }, (_, index) =>
      fetchPage(index + 2)
    )
  );

  return [firstPage, ...remainingPages].flatMap((page) => page.items);
};

export const sortStoryToonEpisodesByOriginalOrder = (
  storyToonEpisodes: IStoryToonEpisode[],
  originalEpisodes: Array<
    Pick<IEpisode, 'id'> &
      Partial<Pick<IEpisode, 'isFree' | 'purchaseType'>>
  >,
  ordering: StoryToonEpisodeOrdering = 'oldest'
) => {
  const originalEpisodeById = new Map(
    originalEpisodes.map((episode, index) => [episode.id, { episode, index }])
  );
  const sortedEpisodes = storyToonEpisodes
    .map((episode, index) => ({
      episode,
      fallbackOrder: index,
      originalEpisode:
        typeof episode.episodeId === 'number'
          ? originalEpisodeById.get(episode.episodeId)
          : undefined,
    }))
    .sort((a, b) => {
      if (a.originalEpisode === undefined && b.originalEpisode === undefined) {
        return a.fallbackOrder - b.fallbackOrder;
      }
      if (a.originalEpisode === undefined) {
        return 1;
      }
      if (b.originalEpisode === undefined) {
        return -1;
      }
      return a.originalEpisode.index - b.originalEpisode.index;
    })
    .map(({ episode, originalEpisode }, index) => ({
      ...episode,
      episodeNumber: index + 1,
      isFree: originalEpisode?.episode.isFree,
      purchaseType: originalEpisode?.episode.purchaseType,
    }));

  return ordering === 'newest' ? sortedEpisodes.reverse() : sortedEpisodes;
};

export const canViewStoryToonEpisode = (
  episode?: Pick<IStoryToonEpisode, 'isFree' | 'purchaseType'>
) => !!episode && (episode.isFree === true || !!episode.purchaseType);

export const fetchStoryToonEpisodesInStoryOrder = async (
  storyToonId: number,
  storyId: number,
  ordering: StoryToonEpisodeOrdering = 'oldest'
) => {
  const [storyToonEpisodes, originalEpisodes] = await Promise.all([
    fetchStoryToonEpisodes(storyToonId, 'oldest'),
    fetchOriginalEpisodes(storyId).catch(() => []),
  ]);

  return sortStoryToonEpisodesByOriginalOrder(
    storyToonEpisodes,
    originalEpisodes,
    ordering
  );
};

export const fetchStoryToonEpisode = async (
  storyToonId: number,
  storyToonEpisodeId: number
) => {
  const { data } = await withStoryToonTimeout(
    api.get<IStoryToonEpisode>(
      `${STORY_TOON_ENDPOINT}/${storyToonId}/episodes/${storyToonEpisodeId}/`
    )
  );

  return data;
};
